// apps/spa/src/hooks/useCountdownTick.ts
// Returns a tick counter that increments every intervalMs while the document is visible.
// Pauses automatically when document.visibilityState becomes 'hidden' (Page Visibility API).
// Resumes when the tab becomes visible again.
//
// One global useCountdownTick(60_000) is used at ReminderTable level — NOT per-row timers.
// Each tick triggers a re-render of any component that consumes this hook, which then
// calls nextRunRelative() with the new Date.now() to display an updated countdown.
//
// Implementation notes:
// - Uses functional setState (setTick(t => t + 1)) to avoid stale closure issues.
// - clearInterval in the visible-effect cleanup is StrictMode-correct:
//   StrictMode double-invokes effects; the cleanup runs between the two invocations,
//   so the interval from the first mount is cleared before the second mount starts.
// - The visibilitychange listener effect runs once on mount and cleans up on unmount.

import { useState, useEffect } from 'react';

/**
 * Returns an incrementing tick number. Increments every `intervalMs` milliseconds
 * while the document is visible. Pauses on visibility hidden; resumes on visible.
 *
 * @param intervalMs Tick interval in milliseconds (default 60_000 = 1 minute)
 * @returns tick number (starts at 0, increments on each interval)
 */
export function useCountdownTick(intervalMs: number = 60_000): number {
  const [tick, setTick] = useState(0);
  const [visible, setVisible] = useState(
    typeof document !== 'undefined' ? document.visibilityState !== 'hidden' : true,
  );

  // Track document visibility
  useEffect(() => {
    function handleVisibilityChange() {
      setVisible(document.visibilityState !== 'hidden');
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // runs once on mount

  // Tick while visible; pause when hidden
  useEffect(() => {
    if (!visible) return; // paused — no interval
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id); // StrictMode-correct cleanup
  }, [visible, intervalMs]);

  return tick;
}
